// ──────────────────────────────────────────────
// Auth store (Zustand 5 with persist)
// ──────────────────────────────────────────────

import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { User } from '@/types/auth';

/** Simplified org info stored in auth state */
interface AuthOrg {
  id: string;
  name: string;
  slug: string;
  plan: string;
  trialEndsAt: string | null;
  subscriptionStatus: string | null;
  subscriptionCancelAt: string | null;
}

interface AuthState {
  /** Currently authenticated user (null when logged out) */
  user: User | null;
  /** JWT access token */
  token: string | null;
  /** JWT refresh token */
  refreshToken: string | null;
  /** Convenience boolean derived from token presence */
  isAuthenticated: boolean;
  /** Current organization the user is operating in */
  org: AuthOrg | null;
  /** User's role slug within the current organization */
  role: string | null;
  /** User's granular permissions from their org role */
  permissions: string[];
}

interface AuthActions {
  /** Set auth data after login / register */
  setAuth: (data: {
    user: User;
    token: string;
    refreshToken: string;
    org?: AuthOrg;
    role?: string;
    permissions?: string[];
  }) => void;
  /** Clear all auth state (logout) */
  logout: () => void;
  /** Partially update the user object (e.g. after profile edit) */
  updateUser: (patch: Partial<User>) => void;
  /** Switch or set the current organization + role + permissions */
  setOrg: (org: AuthOrg, role: string, permissions?: string[]) => void;
  /** Update subscription-related fields on the current org (for live-sync from billing) */
  updateOrgSubscription: (patch: { subscriptionStatus?: string | null; subscriptionCancelAt?: string | null; plan?: string }) => void;
  /** Check if the user has at least one of the given permissions */
  hasPermission: (...required: string[]) => boolean;
}

const initialState: AuthState = {
  user: null,
  token: null,
  refreshToken: null,
  isAuthenticated: false,
  org: null,
  role: null,
  permissions: [],
};

export const useAuthStore = create<AuthState & AuthActions>()(
  persist(
    (set, get) => ({
      ...initialState,

      setAuth: ({ user, token, refreshToken, org, role, permissions }) =>
        set({
          user,
          token,
          refreshToken,
          isAuthenticated: true,
          org: org ?? null,
          role: role ?? null,
          permissions: permissions ?? [],
        }),

      logout: () => set(initialState),

      updateUser: (patch) =>
        set((state) => ({
          user: state.user ? { ...state.user, ...patch } : null,
        })),

      setOrg: (org, role, permissions) => set({ org, role, permissions: permissions ?? [] }),

      updateOrgSubscription: (patch) =>
        set((state) => ({
          org: state.org ? { ...state.org, ...patch } : null,
        })),

      hasPermission: (...required) => {
        const { permissions } = get();
        if (permissions.includes('*')) return true;
        return required.some((p) => permissions.includes(p));
      },
    }),
    {
      name: 'worsaas-auth',
      partialize: (state) => ({
        user: state.user,
        token: state.token,
        refreshToken: state.refreshToken,
        isAuthenticated: state.isAuthenticated,
        org: state.org,
        role: state.role,
        permissions: state.permissions,
      }),
    }
  )
);
